import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Users, Clock3, Layers, Repeat2, Star } from "lucide-react";
import clsx from "clsx";

import type { ProcessSummary } from "../../api/types";
import { getBrowserHash, splitHash, updateBrowserHash, updateHash } from "../../utils/urlHash";
import type { HashParamKey } from "../../utils/urlHash";
import { SearchInput } from "../Inputs";
import PaginationBar from "../PaginationBar";
import UserFilterMultiSelect from "../UserFilterMultiSelect";
import { FiltersPopover, RangeFilter } from "../FilterControls";
import ProcessFeedbackBanner from "./ProcessFeedbackBanner";
import ProcessDirectoryTable from "./ProcessDirectoryTable";
import {
  DEFAULT_PROCESS_SORT,
  compareProcesses,
  groupingKind,
  sortAvailable,
  processPeriodDays,
  readProcessGrouping,
  type ProcessGrouping,
} from "./directoryGrouping";
import { processPeople, processesByPerson } from "./processPeople";
import DashboardDateRange, { type DateRangePreset } from "../dashboard/DashboardDateRange";
import ProcessDisplayMenu from "./ProcessDisplayMenu";
import { useCategories } from "./processGroups";
import {
  DEFAULT_PROCESS_COLUMNS,
  persistFavouriteProcessSlugs,
  persistProcessColumns,
  readFavouriteProcessSlugs,
  readProcessColumns,
  type ProcessColumn,
} from "./processPreferences";

type Range = [number, number];
type NullableRange = [number | null, number | null];

// Hash param keys owned by this section (registered in urlHash's HashParamKey).
export const PROCESS_PAGE_HASH_KEY = "page";
export const PROCESS_SEARCH_HASH_KEY = "ps";
export const PROCESS_SORT_HASH_KEY = "psort";
export const PROCESS_DURATION_MIN_HASH_KEY = "pdMin";
export const PROCESS_DURATION_MAX_HASH_KEY = "pdMax";
export const PROCESS_STEPS_MIN_HASH_KEY = "pstMin";
export const PROCESS_STEPS_MAX_HASH_KEY = "pstMax";
export const PROCESS_FREQUENCY_MIN_HASH_KEY = "pfMin";
export const PROCESS_FREQUENCY_MAX_HASH_KEY = "pfMax";
export const PROCESS_FAVOURITES_HASH_KEY = "pfav";
export const PROCESS_GROUPS_HASH_KEY = "pg";
// Retained for bookmarks created with the old Shared only filter.
export const PROCESS_SHARED_HASH_KEY = "pshared";
export const PROCESS_PEOPLE_MIN_HASH_KEY = "ppMin";
export const PROCESS_PEOPLE_MAX_HASH_KEY = "ppMax";

type ProcessFilters = {
  search: string;
  sort: string;
  duration: NullableRange;
  steps: NullableRange;
  frequency: NullableRange;
  favouritesOnly: boolean;
  grouping: ProcessGrouping;
  people: NullableRange;
};

type DataRange = { duration: Range; steps: Range; frequency: Range };

const computeDataRange = (processes: ProcessSummary[]): DataRange => {
  if (processes.length === 0) {
    return { duration: [0, 1], steps: [0, 1], frequency: [0, 1] };
  }
  let dMin = Infinity,
    dMax = -Infinity;
  let sMin = Infinity,
    sMax = -Infinity;
  let fMin = Infinity,
    fMax = -Infinity;
  for (const p of processes) {
    if (p.avgDuration < dMin) dMin = p.avgDuration;
    if (p.avgDuration > dMax) dMax = p.avgDuration;
    if (p.stepCount < sMin) sMin = p.stepCount;
    if (p.stepCount > sMax) sMax = p.stepCount;
    if (p.frequency < fMin) fMin = p.frequency;
    if (p.frequency > fMax) fMax = p.frequency;
  }
  return {
    duration: [Math.floor(Math.max(0, dMin)), Math.ceil(Math.max(0, dMax))],
    steps: [sMin, sMax],
    frequency: [fMin, fMax],
  };
};

const defaultFilters = (): ProcessFilters => ({
  search: "",
  sort: DEFAULT_PROCESS_SORT,
  duration: [null, null],
  steps: [null, null],
  frequency: [null, null],
  favouritesOnly: false,
  grouping: "domain",
  people: [null, null],
});

const countActive = (filters: ProcessFilters): number => {
  let count = 0;
  if (filters.duration[0] !== null || filters.duration[1] !== null) count++;
  if (filters.steps[0] !== null || filters.steps[1] !== null) count++;
  if (filters.frequency[0] !== null || filters.frequency[1] !== null) count++;
  if (filters.favouritesOnly) count++;
  if (filters.people[0] !== null || filters.people[1] !== null) count++;
  return count;
};

const inRange = (value: number, [min, max]: NullableRange): boolean =>
  (min === null || value >= min) && (max === null || value <= max);

const applyFilters = (
  processes: ProcessSummary[],
  filters: ProcessFilters,
  favouriteSlugs: ReadonlySet<string>,
): ProcessSummary[] => {
  const query = filters.search.toLowerCase();
  return processes.filter(p => {
    if (filters.favouritesOnly && !favouriteSlugs.has(p.slug)) return false;
    if (!inRange(processPeople(p).length, filters.people)) return false;
    if (query) {
      const inName = p.name.toLowerCase().includes(query);
      const inDesc = p.description.toLowerCase().includes(query);
      const inAlias = p.aliases.some(a => a.toLowerCase().includes(query));
      if (!inName && !inDesc && !inAlias) return false;
    }
    if (!inRange(p.avgDuration, filters.duration)) return false;
    if (!inRange(p.stepCount, filters.steps)) return false;
    if (!inRange(p.frequency, filters.frequency)) return false;
    return true;
  });
};

type ProcessesSectionProps = {
  processes: ProcessSummary[] | null;
  totalProcessCount: number | null;
  error: string | null;
  activeUserIds: string[];
  userOptions?: Array<{ id: string; label: string }>;
  onUserChange?: (userIds: string[]) => void;
  showPeople?: boolean;
  personalView?: boolean;
  from?: string;
  to?: string;
  datePreset?: DateRangePreset;
  rememberedCustomRange?: { from: string; to: string };
  onDateChange?: (from: string, to: string, preset?: DateRangePreset) => void;
};

const PROCESS_PAGE_SIZE = 10;

const readFilters = (): ProcessFilters => {
  const { params } = splitHash(getBrowserHash());
  const num = (key: HashParamKey): number | null => {
    const raw = params.get(key);
    const n = raw === null ? NaN : Number(raw);
    return isNaN(n) ? null : n;
  };
  return {
    search: params.get(PROCESS_SEARCH_HASH_KEY) ?? "",
    sort: params.get(PROCESS_SORT_HASH_KEY) ?? DEFAULT_PROCESS_SORT,
    duration: [num(PROCESS_DURATION_MIN_HASH_KEY), num(PROCESS_DURATION_MAX_HASH_KEY)],
    steps: [num(PROCESS_STEPS_MIN_HASH_KEY), num(PROCESS_STEPS_MAX_HASH_KEY)],
    frequency: [num(PROCESS_FREQUENCY_MIN_HASH_KEY), num(PROCESS_FREQUENCY_MAX_HASH_KEY)],
    favouritesOnly: params.get(PROCESS_FAVOURITES_HASH_KEY) === "1",
    grouping: readProcessGrouping(params.get(PROCESS_GROUPS_HASH_KEY)),
    people: [
      num(PROCESS_PEOPLE_MIN_HASH_KEY) ?? (params.get(PROCESS_SHARED_HASH_KEY) === "1" ? 2 : null),
      num(PROCESS_PEOPLE_MAX_HASH_KEY),
    ],
  };
};

const ProcessesSection = ({
  processes,
  totalProcessCount,
  error,
  activeUserIds,
  userOptions,
  onUserChange,
  showPeople = true,
  personalView = !showPeople,
  from = "",
  to = "",
  onDateChange,
  datePreset,
  rememberedCustomRange,
}: ProcessesSectionProps) => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const multiplePeople = showPeople && activeUserIds.length > 1;
  const processItems = useMemo(() => processes ?? [], [processes]);
  const [favouriteProcessSlugs, setFavouriteProcessSlugs] = useState(readFavouriteProcessSlugs);
  const favouriteProcessSlugSet = useMemo(
    () => new Set(favouriteProcessSlugs),
    [favouriteProcessSlugs],
  );

  const [columns, setColumns] = useState(readProcessColumns);
  const changeColumns = (next: readonly ProcessColumn[]) => {
    setColumns(next);
    persistProcessColumns(next);
  };

  const [filters, setFilters] = useState<ProcessFilters>(readFilters);
  const categories = useCategories();
  const personProcesses = useMemo(
    () =>
      filters.grouping === "person" && processes
        ? processesByPerson(processes, activeUserIds)
        : null,
    [filters.grouping, processes, activeUserIds],
  );
  const rangeItems = useMemo(
    () => (personProcesses ? Object.values(personProcesses).flat() : processItems),
    [personProcesses, processItems],
  );
  const dataRange = useMemo(() => computeDataRange(rangeItems), [rangeItems]);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  const [restoredLocationKey, setRestoredLocationKey] = useState(location.key);
  useEffect(() => {
    setFilters(readFilters());
    setRestoredLocationKey(location.key);
  }, [location.key]);

  const persistFilters = (f: ProcessFilters) => {
    // Unset (null) filters drop their key from the hash.
    const str = (value: number | null): string | null => (value !== null ? String(value) : null);
    updateBrowserHash({
      [PROCESS_PAGE_HASH_KEY]: null,
      [PROCESS_SEARCH_HASH_KEY]: f.search || null,
      [PROCESS_SORT_HASH_KEY]: f.sort === DEFAULT_PROCESS_SORT ? null : f.sort,
      [PROCESS_DURATION_MIN_HASH_KEY]: str(f.duration[0]),
      [PROCESS_DURATION_MAX_HASH_KEY]: str(f.duration[1]),
      [PROCESS_STEPS_MIN_HASH_KEY]: str(f.steps[0]),
      [PROCESS_STEPS_MAX_HASH_KEY]: str(f.steps[1]),
      [PROCESS_FREQUENCY_MIN_HASH_KEY]: str(f.frequency[0]),
      [PROCESS_FREQUENCY_MAX_HASH_KEY]: str(f.frequency[1]),
      [PROCESS_FAVOURITES_HASH_KEY]: f.favouritesOnly ? "1" : null,
      [PROCESS_GROUPS_HASH_KEY]:
        f.grouping === "domain" ? null : f.grouping === "none" ? "0" : f.grouping,
      [PROCESS_SHARED_HASH_KEY]: null,
      [PROCESS_PEOPLE_MIN_HASH_KEY]: str(f.people[0]),
      [PROCESS_PEOPLE_MAX_HASH_KEY]: str(f.people[1]),
    });
  };

  const grouping = filters.grouping;
  const showPeopleColumn = multiplePeople && grouping !== "person";
  // A bookmarked sort that does not apply to this view (e.g. "Most shared" for one person).
  const sort = sortAvailable(filters.sort, grouping, multiplePeople)
    ? filters.sort
    : DEFAULT_PROCESS_SORT;
  const teamBySlug = useMemo(
    () => new Map(processItems.map(process => [process.slug, process])),
    [processItems],
  );
  const filterAndSort = (items: ProcessSummary[], scopedToPerson = false) =>
    applyFilters(
      items,
      {
        ...filters,
        people: multiplePeople && !scopedToPerson ? filters.people : [null, null],
      },
      favouriteProcessSlugSet,
    ).sort(
      compareProcesses(
        sort,
        multiplePeople && !scopedToPerson,
        scopedToPerson ? teamBySlug : undefined,
      ),
    );
  const scopedPeople = Object.fromEntries(
    Object.entries(personProcesses ?? {}).map(([id, items]) => [id, filterAndSort(items, true)]),
  );
  const filtered =
    filters.grouping === "person"
      ? [
          ...new Map(
            Object.values(scopedPeople)
              .flat()
              .map(process => [process.slug, process]),
          ).values(),
        ]
      : filterAndSort(processItems);
  const grouped = grouping !== "none";
  const displayModified =
    grouping !== defaultFilters().grouping ||
    sort !== defaultFilters().sort ||
    columns.join() !== DEFAULT_PROCESS_COLUMNS.join();
  // Use the full people/date scope: searching and numeric filters must not change N.
  const periodDays = processPeriodDays(processItems, from, to);
  const groupsLoading =
    (grouping === "person" && personProcesses === null) ||
    (!!groupingKind(grouping) && categories === null);
  const peopleFiltered =
    !!onUserChange &&
    !!userOptions?.length &&
    (activeUserIds.length !== userOptions.length ||
      userOptions.some(option => !activeUserIds.includes(option.id)));
  const activeCount = countActive({
    ...filters,
    people: multiplePeople && grouping !== "person" ? filters.people : [null, null],
  });

  const rawPage = splitHash(getBrowserHash()).params.get(PROCESS_PAGE_HASH_KEY);
  const requestedPage = Number(rawPage);
  const totalPages = grouped ? 1 : Math.max(1, Math.ceil(filtered.length / PROCESS_PAGE_SIZE));
  const page =
    Number.isSafeInteger(requestedPage) && requestedPage > 0
      ? Math.min(requestedPage, totalPages)
      : 1;
  const pagination = {
    page,
    totalPages,
    totalItems: filtered.length,
    pageSize: PROCESS_PAGE_SIZE,
  };
  const paginatedItems = filtered.slice((page - 1) * PROCESS_PAGE_SIZE, page * PROCESS_PAGE_SIZE);

  useEffect(() => {
    // Wait for data and restored filters before clamping a bookmarked page.
    if (processes === null || restoredLocationKey !== location.key) return;
    const canonical = page === 1 ? null : String(page);
    if (rawPage !== canonical) updateBrowserHash({ [PROCESS_PAGE_HASH_KEY]: canonical });
  }, [processes, restoredLocationKey, location.key, page, rawPage]);

  const changePage = (nextPage: number) => {
    navigate({
      pathname: location.pathname,
      search: location.search,
      hash: updateHash(getBrowserHash(), {
        [PROCESS_PAGE_HASH_KEY]: nextPage === 1 ? null : String(nextPage),
      }),
    });
  };

  const changePeople = (userIds: string[]) => {
    updateBrowserHash({ [PROCESS_PAGE_HASH_KEY]: null });
    onUserChange?.(userIds);
  };

  const commitFilters = (next: ProcessFilters) => {
    filtersRef.current = next;
    setFilters(next);
    persistFilters(next);
  };

  const updateFilter = <K extends keyof ProcessFilters>(key: K, value: ProcessFilters[K]) =>
    // A debounced search must preserve controls changed since the keystroke.
    commitFilters({ ...filtersRef.current, [key]: value });

  const clearFilters = () => {
    commitFilters({
      ...defaultFilters(),
      grouping: filters.grouping,
      sort: filters.sort,
      search: filters.search,
    });
  };

  const resetDisplay = () => {
    commitFilters({
      ...filtersRef.current,
      grouping: defaultFilters().grouping,
      sort: defaultFilters().sort,
    });
    changeColumns(DEFAULT_PROCESS_COLUMNS);
  };

  const toggleFavouriteProcess = (slug: string) => {
    setFavouriteProcessSlugs(current => {
      const next = current.includes(slug)
        ? current.filter(favouriteSlug => favouriteSlug !== slug)
        : [...current, slug];
      persistFavouriteProcessSlugs(next);
      return next;
    });
  };

  const isFiltered = activeCount > 0 || !!filters.search || peopleFiltered || !!(from || to);
  const subtitle =
    processes && totalProcessCount !== null
      ? isFiltered
        ? t("showing_n_of_total", {
            filtered: filtered.length,
            total: totalProcessCount,
          })
        : t("processes_across_your_recordings", { count: filtered.length })
      : t("loading_ellipsis");

  const rangeFilters = [
    {
      key: "duration" as const,
      label: t("process_directory.avg_duration"),
      icon: <Clock3 className="h-4 w-4" />,
      bounds: dataRange.duration,
      duration: true,
    },
    {
      key: "steps" as const,
      label: t("step_count"),
      icon: <Layers className="h-4 w-4" />,
      bounds: dataRange.steps,
    },
    {
      key: "frequency" as const,
      label: t("process_directory.case_count"),
      icon: <Repeat2 className="h-4 w-4" />,
      bounds: dataRange.frequency,
    },
    ...(multiplePeople && grouping !== "person"
      ? [
          {
            key: "people" as const,
            label: t("filterControls.people_count"),
            icon: <Users className="h-4 w-4" />,
            bounds: [1, Math.max(1, activeUserIds.length)],
          },
        ]
      : []),
  ];
  return (
    <section>
      <div className="dashboard-page-header mb-3">
        <div className="min-w-64 flex-1">
          <h1 className="dashboard-page-title">{t("processes")}</h1>
          <p className="dashboard-page-description">
            {t(
              personalView
                ? "process_directory.subtitle_personal"
                : "process_directory.subtitle_team",
            )}
          </p>
        </div>
        <div className="ml-auto flex max-w-full flex-wrap items-start justify-end gap-3 self-stretch">
          {showPeople && (userOptions?.length ?? 0) > 0 && onUserChange && (
            <div className="dashboard-people-filter min-w-40">
              <UserFilterMultiSelect
                allLabel={t("watchlist.all_people")}
                icon={<Users aria-hidden="true" className="h-4 w-4 shrink-0 text-text-dim" />}
                value={activeUserIds}
                options={userOptions ?? []}
                onChange={changePeople}
              />
            </div>
          )}
          {onDateChange && (
            <div className="shrink-0 [&>button]:shadow-none">
              <DashboardDateRange
                activityUserIds={activeUserIds}
                from={from}
                to={to}
                preset={datePreset}
                rememberedCustomRange={rememberedCustomRange}
                onChange={onDateChange}
              />
            </div>
          )}
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="w-full sm:w-80 [&_input]:h-[38px] [&_input]:w-full [&_input]:shadow-none">
          <SearchInput
            value={filters.search}
            onChange={v => updateFilter("search", v)}
            placeholder={t("search_in_processes")}
          />
        </div>
        {(processes !== null || !!userOptions?.length) && (
          <FiltersPopover count={activeCount} onClear={clearFilters} align="start">
            <button
              type="button"
              aria-pressed={filters.favouritesOnly}
              title={t("process_directory.favourites_hint")}
              onClick={() => updateFilter("favouritesOnly", !filters.favouritesOnly)}
              className={clsx(
                "dropdown-trigger justify-start",
                filters.favouritesOnly && "filter-active",
              )}
            >
              <Star
                aria-hidden="true"
                className={clsx(
                  "h-4 w-4",
                  filters.favouritesOnly ? "fill-info text-info" : "text-text-dim",
                )}
              />
              {t("favourites")}
            </button>
            {rangeFilters.map(item => (
              <RangeFilter
                key={item.key}
                label={item.label}
                icon={item.icon}
                rangeMin={item.bounds[0]}
                rangeMax={item.bounds[1]}
                duration={item.duration}
                value={filters[item.key]}
                onChange={value => updateFilter(item.key, value)}
              />
            ))}
          </FiltersPopover>
        )}
        <div className="ml-auto">
          <ProcessDisplayMenu
            grouping={grouping}
            sort={sort}
            columns={columns}
            multiplePeople={multiplePeople}
            modified={displayModified}
            onGroupingChange={value => updateFilter("grouping", value)}
            onSortChange={value => updateFilter("sort", value)}
            onColumnsChange={changeColumns}
            onReset={resetDisplay}
          />
        </div>
      </div>

      {error && (
        <div className="rounded-xl border border-danger/30 bg-danger/10 p-4 text-sm text-danger-strong">
          {t("failed_to_load_processes")}: {error}
        </div>
      )}

      {!error && (processes === null || groupsLoading) && (
        <p role="status" className="py-8 text-center text-sm text-text-dim">
          {t("loading_ellipsis")}
        </p>
      )}

      {!error && !groupsLoading && processes !== null && processes.length === 0 && (
        <div className="rounded-xl border border-outline bg-surface p-4 text-sm text-text-dim shadow-sm">
          {t(
            from || to || peopleFiltered
              ? "no_results_match_filters"
              : "no_processes_discovered_yet_record_some_sessions_first",
          )}
        </div>
      )}

      {!error &&
        !groupsLoading &&
        processes !== null &&
        processes.length > 0 &&
        filtered.length === 0 && (
          <div className="rounded-xl border border-outline bg-surface p-4 text-sm text-text-dim shadow-sm">
            {t("no_results_match_filters")}
          </div>
        )}

      {!error && !groupsLoading && processes !== null && isFiltered && (
        <p role="status" className="mb-3 text-xs text-text-dim">
          {subtitle}
        </p>
      )}
      {!error && !groupsLoading && processes !== null && filtered.length > 0 && (
        <>
          <ProcessDirectoryTable
            key={`${grouping}:${from}:${to}:${filters.search}:${filters.people.join(":")}:${filters.favouritesOnly}:${activeUserIds.join(",")}`}
            processes={grouped ? filtered : paginatedItems}
            grouping={grouping}
            personProcesses={scopedPeople}
            scopeProcesses={processItems}
            sort={sort}
            onSortChange={value => updateFilter("sort", value)}
            periodDays={periodDays}
            columns={columns}
            showPeople={showPeopleColumn}
            userOptions={userOptions ?? []}
            activeUserIds={activeUserIds}
            favouriteSlugs={favouriteProcessSlugSet}
            onToggleFavourite={toggleFavouriteProcess}
            categories={categories ?? undefined}
          />
          <p className="mt-3 text-xs text-text-dim" role="status">
            {subtitle}
          </p>
          {pagination.totalPages > 1 && (
            <PaginationBar
              className="mt-4"
              page={pagination.page}
              totalPages={pagination.totalPages}
              totalItems={pagination.totalItems}
              pageSize={pagination.pageSize}
              onPageChange={changePage}
            />
          )}
        </>
      )}
      <ProcessFeedbackBanner />
    </section>
  );
};

export default ProcessesSection;
